import styled from '@emotion/styled'
import { Box, IconButton, Modal, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { PlusCircle, Trash2 } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Label from '../../components/form/Label'
import { SERVER_URL, apiHeader } from '../../config'
import UserContext from '../../context/UserContext'
import { Exercise, Intensity } from '../../types/exercise.type'
import { TrainingSessionGroup } from '../../types/training-session-group.type'
import { StyledButton } from '../admin/Login'
import { AdminForm } from '../admin/users/AdminUsersForm'
import { TrainingSession } from './CoachTrainingSession'

interface TrainingSessionStep {
    id?: number
    title: string
    description?: string
    startTime: string
    endTime: string
    exerciseId?: number
}

interface TrainingSessionFormData {
    id?: number
    title: string
    description?: string
    intensity: Intensity
    steps: TrainingSessionStep[]
    groupId?: number
    order: number
    createdAt?: string
    updatedAt?: string
}

interface CoachTrainingSessionFormModalProps {
    isOpen: boolean
    onClose: () => void
    session: TrainingSession | null
    refetchGroups: () => void
}

const defaultValues: TrainingSessionFormData = {
    title: 'A définir',
    description: 'A définir',
    intensity: Intensity.MEDIUM,
    order: 1,
    steps: [
        {
            title: 'Échauffement',
            description: 'Courir 3 tours, puis continuer en faisant des gammes sur les largeurs',
            startTime: '19:00',
            endTime: '19:20',
            exerciseId: undefined,
        },
        {
            title: 'Physique',
            description: 'À définir',
            startTime: '19:20',
            endTime: '19:40',
            exerciseId: undefined,
        },
        {
            title: 'Exercice Technique de déplacement',
            description: 'À définir',
            startTime: '19:40',
            endTime: '20:10',
            exerciseId: undefined,
        },
        {
            title: 'Exercice Technique raquette',
            description: 'À définir',
            startTime: '20:10',
            endTime: '21:00',
            exerciseId: undefined,
        },
    ],
}

const CoachTrainingSessionFormModal: React.FC<CoachTrainingSessionFormModalProps> = ({
    isOpen,
    onClose,
    session,
    refetchGroups,
}) => {
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formData, setFormData] = useState<TrainingSessionFormData>({
        title: 'A définir',
        description: 'A définir',
        intensity: Intensity.MEDIUM,
        order: 1,
        groupId: undefined,
        steps: [
            {
                title: 'Échauffement',
                description: 'Courir 3 tours, puis continuer en faisant des gammes sur les largeurs',
                startTime: '19:00',
                endTime: '19:20',
                exerciseId: undefined,
            },
            {
                title: 'Physique',
                description: 'À définir',
                startTime: '19:20',
                endTime: '19:40',
                exerciseId: undefined,
            },
            {
                title: 'Exercice Technique de déplacement',
                description: 'À définir',
                startTime: '19:40',
                endTime: '20:10',
                exerciseId: undefined,
            },
            {
                title: 'Exercice Technique raquette',
                description: 'À définir',
                startTime: '20:10',
                endTime: '21:00',
                exerciseId: undefined,
            },
        ],
    })
    const userContext = useContext(UserContext)
    const user = userContext as { tokenCoach: string; coachData: { id: number } } | null

    // Récupération des groupes de sessions
    const { data: groups = [] } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups'],
        queryFn: async (): Promise<TrainingSessionGroup[]> => {
            if (!user) throw new Error('User not authenticated')
            const headers = apiHeader(user.tokenCoach)
            const response = await axios.get<TrainingSessionGroup[]>(`${SERVER_URL}/training-session-groups`, headers)

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch training session groups')
            }

            return response.data
        },
        enabled: !!user,
    })

    // Récupération des exercices
    const { data: exercises = [] } = useQuery<Exercise[]>({
        queryKey: ['exercises'],
        queryFn: async (): Promise<Exercise[]> => {
            if (!user) throw new Error('User not authenticated')
            const headers = apiHeader(user.tokenCoach)
            const response = await axios.get<Exercise[]>(`${SERVER_URL}/exercises`, headers)

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch exercises')
            }

            return response.data
        },
        enabled: !!user,
    })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleStepChange = <K extends keyof TrainingSessionStep>(
        index: number,
        field: K,
        value: string | number | undefined
    ) => {
        setFormData((prev) => {
            const newSteps = [...prev.steps]

            let processedValue: string | number | undefined = value

            if (field === 'exerciseId') {
                processedValue = value !== undefined && value !== '' ? Number(value) : undefined
            }

            newSteps[index] = {
                ...newSteps[index],
                [field]: processedValue as TrainingSessionStep[K],
            }

            return { ...prev, steps: newSteps }
        })
    }

    const addStep = () => {
        const lastStep = formData.steps[formData.steps.length - 1]
        const lastEndTime = lastStep ? lastStep.endTime : '09:00'

        const [hours, minutes] = lastEndTime.split(':').map(Number)
        const newEndTime = new Date()
        newEndTime.setHours(hours + 1, minutes)
        const formattedEndTime = `${String(newEndTime.getHours()).padStart(2, '0')}:${String(
            newEndTime.getMinutes()
        ).padStart(2, '0')}`

        setFormData((prev) => ({
            ...prev,
            steps: [
                ...prev.steps,
                {
                    title: 'Nouvelle étape',
                    description: '',
                    startTime: lastEndTime,
                    endTime: formattedEndTime,
                    exerciseId: undefined,
                },
            ],
        }))
    }

    const removeStep = (index: number) => {
        if (formData.steps.length <= 1) return
        setFormData((prev) => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index),
        }))
    }

    useEffect(() => {
        if (!isOpen) {
            setFormData(defaultValues)
            return
        }

        if (session) {
            const formattedSteps =
                session.steps?.length > 0
                    ? session.steps.map((step) => ({
                          id: step.id,
                          title: step.title ?? 'Nouvelle étape',
                          description: step.description ?? '',
                          startTime: step.startTime ?? '09:00',
                          endTime: step.endTime ?? '10:00',
                          groupId: session.groupId ? +session.groupId : undefined,
                          exerciseId: step.exerciseId ?? undefined,
                      }))
                    : [...defaultValues.steps]

            setFormData({
                id: session.id,
                title: session.title ?? defaultValues.title,
                order: session.order ? +session.order : +defaultValues.order,
                description: session.description ?? defaultValues.description,
                intensity: session.intensity ?? defaultValues.intensity,
                groupId: session.groupId ? +session.groupId : undefined,
                steps: formattedSteps,
                createdAt: session.createdAt,
                updatedAt: session.updatedAt,
            })
        } else {
            setFormData({
                ...defaultValues,
            })
        }
    }, [session, isOpen])

    type CreateTrainingSessionData = Omit<TrainingSessionFormData, 'id' | 'createdAt' | 'updatedAt'>

    const createMutation = useMutation<unknown, AxiosError<{ message?: string }>, CreateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const headers = apiHeader(user.tokenCoach)
            const response = await axios.post<unknown>(`${SERVER_URL}/training-sessions`, data, headers)

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to create training session')
            }

            return response.data
        },
        onSuccess: () => {
            refetchGroups()
            toast.success(t('coach.trainingSessions.createdSuccess'))
            onClose()
        },
        onError: (error) => {
            console.error('Error creating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled: () => {
            setIsSubmitting(false)
        },
    })

    type UpdateTrainingSessionData = Omit<TrainingSessionFormData, 'coachId' | 'createdAt' | 'updatedAt'> & {
        id: number
    }

    const updateMutation = useMutation<unknown, AxiosError<{ message?: string }>, UpdateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const headers = apiHeader(user.tokenCoach)
            const response = await axios.patch<unknown>(`${SERVER_URL}/training-sessions/${data.id}`, data, {
                headers,
            })

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to update training session')
            }

            return response.data
        },
        onSuccess: () => {
            refetchGroups()
            toast.success(t('coach.trainingSessions.updatedSuccess'))
            onClose()
        },
        onError: (error) => {
            console.error('Error updating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled: () => {
            setIsSubmitting(false)
        },
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user) {
            toast.error(t('common.authRequired'))
            return
        }

        if (isSubmitting) {
            return
        }

        setIsSubmitting(true)

        try {
            if (!formData.title.trim()) {
                toast.error(t('coach.trainingSessions.errors.titleRequired'))
                return
            }

            for (let i = 0; i < formData.steps.length; i++) {
                const step = formData.steps[i]
                if (!step.title.trim()) {
                    toast.error(t('coach.trainingSessions.errors.stepTitleRequired', { index: i + 1 }))
                    return
                }

                const [startHours, startMinutes] = step.startTime.split(':').map(Number)
                const [endHours, endMinutes] = step.endTime.split(':').map(Number)

                if (endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)) {
                    toast.error(t('coach.trainingSessions.errors.invalidTimeRange', { index: i + 1 }))
                    return
                }

                for (let j = 0; j < formData.steps.length; j++) {
                    if (i === j) continue

                    const otherStep = formData.steps[j]
                    const [otherStartHours, otherStartMinutes] = otherStep.startTime.split(':').map(Number)
                    const [otherEndHours, otherEndMinutes] = otherStep.endTime.split(':').map(Number)

                    if (
                        (startHours < otherEndHours ||
                            (startHours === otherEndHours && startMinutes < otherEndMinutes)) &&
                        (endHours > otherStartHours || (endHours === otherStartHours && endMinutes > otherStartMinutes))
                    ) {
                        toast.error(t('coach.trainingSessions.errors.overlappingSteps', { step1: i + 1, step2: j + 1 }))
                        return
                    }
                }
            }

            const submissionData = {
                ...formData,
                order: +formData.order,
                groupId: formData.groupId ? +formData.groupId : undefined,
                steps: formData.steps.map(({ exerciseId, ...step }) => ({
                    ...step,
                    ...(exerciseId !== undefined && { exerciseId: Number(exerciseId) }),
                })),
            }

            if (session?.id) {
                await updateMutation.mutateAsync({
                    ...submissionData,
                    id: session.id,
                    steps: submissionData.steps.map((step) => ({
                        ...step,
                        exerciseId: step.exerciseId ? Number(step.exerciseId) : undefined,
                    })),
                })
            } else {
                const cleanData: CreateTrainingSessionData = {
                    ...submissionData,
                    description: submissionData.description?.trim() ?? undefined,
                    steps: submissionData.steps.map((step) => ({
                        ...step,
                        description: step.description?.trim() ?? undefined,
                        exerciseId: step.exerciseId ? Number(step.exerciseId) : undefined,
                    })),
                }

                await createMutation.mutateAsync(cleanData)
            }
        } catch (error) {
            console.error('Error submitting form:', error)
            const errorMessage = error instanceof Error ? error.message : t('common.errorOccurred')
            toast.error(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal open={isOpen} onClose={onClose}>
            <ModalContent>
                <Typography variant="h5" component="h2" gutterBottom>
                    {session ? t('coach.trainingSessions.editSession') : t('coach.trainingSessions.newSession')}
                </Typography>

                <StyledAdminForm onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Label>{t('coach.trainingSessions.title')} *</Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Label>{t('coach.trainingSessions.description')}</Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description ?? ''}
                            onChange={handleInputChange}
                            rows={4}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="row">
                            <div className="col-md-6">
                                <Label>{t('coach.trainingSessions.intensity')} *</Label>
                                <Form.Select
                                    name="intensity"
                                    value={formData.intensity}
                                    onChange={handleInputChange}
                                    required
                                    className="form-select"
                                >
                                    {Object.values(Intensity).map((intensity) => (
                                        <option key={intensity} value={intensity}>
                                            {t(`intensity.${intensity.toLowerCase()}`)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="col-md-6">
                                <Label>Ordre</Label>
                                <Form.Control
                                    type="number"
                                    name="order"
                                    min="1"
                                    value={+formData.order}
                                    onChange={handleInputChange}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Label>{t('coach.trainingSessions.group')}</Label>
                        <Form.Select name="groupId" value={formData.groupId ?? ''} onChange={handleInputChange}>
                            {!groups.find((group) => group.title === 'Aucun groupe') && (
                                <option value="">{t('coach.trainingSessions.noGroup')}</option>
                            )}
                            {groups.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.title}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Label>{t('coach.trainingSessions.sessionSteps')}</Label>
                            <button
                                type="button"
                                onClick={addStep}
                                className="btn btn-link p-0"
                                style={{ fontSize: '1.6rem' }}
                            >
                                <PlusCircle size={20} className="me-1" />
                                {t('coach.trainingSessions.addStepButton')}
                            </button>
                        </div>

                        <div className="steps-container">
                            {formData.steps.map((step, index) => (
                                <div key={`step-card-${index}`} className="step-card mb-3 p-3 border rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">
                                            {t('coach.trainingSessions.stepNumber', { number: index + 1 })}
                                        </h6>
                                        <div>
                                            <IconButton
                                                size="small"
                                                onClick={() => removeStep(index)}
                                                disabled={formData.steps.length <= 1}
                                                title={t('coach.trainingSessions.deleteStep')}
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </div>
                                    </div>

                                    <Form.Group className="mb-3">
                                        <Label>{t('coach.trainingSessions.stepTitle')}</Label>
                                        <Form.Control
                                            type="text"
                                            value={step.title}
                                            onChange={(e) => handleStepChange(index, 'title', e.target.value)}
                                            required
                                        />
                                    </Form.Group>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Label>{t('coach.trainingSessions.stepStartTime')}</Label>
                                                <Form.Control
                                                    type="time"
                                                    value={step.startTime}
                                                    onChange={(e) =>
                                                        handleStepChange(index, 'startTime', e.target.value)
                                                    }
                                                    required
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="mb-3">
                                                <Label>{t('coach.trainingSessions.stepEndTime')}</Label>
                                                <Form.Control
                                                    type="time"
                                                    value={step.endTime}
                                                    onChange={(e) => handleStepChange(index, 'endTime', e.target.value)}
                                                    min={step.startTime}
                                                    required
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>

                                    <Form.Group className="mb-3">
                                        <Label>{t('coach.trainingSessions.stepExercise')}</Label>
                                        <Form.Select
                                            value={step.exerciseId ?? ''}
                                            onChange={(e) => handleStepChange(index, 'exerciseId', e.target.value)}
                                        >
                                            <option value="">{t('coach.trainingSessions.noExercise')}</option>
                                            {exercises.map((exercise) => (
                                                <option key={exercise.id} value={exercise.id}>
                                                    {exercise.title}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group>
                                        <Label>{t('coach.trainingSessions.stepDescription')}</Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={step.description ?? ''}
                                            onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <StyledButton type="button" variant="outline-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </StyledButton>
                        <StyledButton type="submit" variant="primary" disabled={isSubmitting}>
                            {isSubmitting ? '...' : session ? t('common.update') : t('common.create')}
                        </StyledButton>
                    </div>
                </StyledAdminForm>
            </ModalContent>
        </Modal>
    )
}

const StyledAdminForm = styled(AdminForm)`
    margin-top: 20px;
`

const ModalContent = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    background-color: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 24px;
    border-radius: 8px;
    overflow-y: auto;

    .step-card {
        background-color: #f8f9fa;
        transition: all 0.2s;

        &:hover {
            background-color: #f1f3f5;
        }
    }

    .btn-link {
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        color: var(--primary-color);

        &:hover {
            color: var(--primary-dark);
        }
    }
`

export default CoachTrainingSessionFormModal
